// src/pages/Dashboard/index.jsx
import React, { useState, useRef } from "react";
import { Row, Col, Spin, DatePicker, Tag } from "antd";
import {
    DashboardContainer,
    ChartContainer,
} from './styled';
import DashboardFilters from "../../components/DashboardFilters";
import BaseTypography from "../../components/BaseTypography";
import BottomNavBar from "../../components/BottomNavBar";
import Header from "../../components/Header";
import { LoadingOutlined } from '@ant-design/icons';
import {
    XAxis,
    Tooltip,
    AreaChart,
    Area,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
} from 'recharts';
import { MetricCardComponent } from './MetricCard';
import { useMetrics } from './useMetrics';
import { useOrders } from './useOrders';
import { useBookings } from './useBookings';
import OrderFulfillmentModal from './OrderFulfillmentModal';
import FulfillmentDetailsModal from './FulfillmentDetailsModal';
import OrdersOverview from './OrdersOverview';

const { RangePicker } = DatePicker;

const Dashboard = () => {
    const bookingsSectionRef = useRef(null);
    const ordersSectionRef = useRef(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showFulfillmentDetailsModal, setShowFulfillmentDetailsModal] = useState(false);
    const [selectedFulfillmentOrder, setSelectedFulfillmentOrder] = useState(null);
    const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);

    const {
        timeframe,
        chartDataKey,
        setChartDataKey,
        filteredData,
        loading: metricsLoading,
        totalMetrics,
        handleFilterChange,
    } = useMetrics();

    const {
        unfulfilledOrders,
        fulfilledOrders,
        loading: ordersLoading,
        updateOrderStatus,
        refreshOrders
    } = useOrders();

    const { bookings = [], loading: bookingsLoading, refreshBookings } = useBookings();

    const loading = metricsLoading || ordersLoading || bookingsLoading;

    // Split bookings into two groups
    const upcomingOrConfirmed = bookings.filter((b) => b.status !== 'completed');

    const completed = bookings.filter((b) => b.status === 'completed');

    const handleViewFulfillment = (order) => {
        setSelectedFulfillmentOrder(order);
        setShowFulfillmentDetailsModal(true);
    };

    const handleDateRangeChange = (dates) => {
        if (!dates) {
            setSelectedDateRange(null);
            handleFilterChange("7D");
            return;
        }
        setSelectedDateRange(dates);
        handleFilterChange("Custom Range", dates);
    };

    const handleFulfillOrder = (order) => {
        setSelectedOrder(order);
        setShowFulfillmentModal(true);
    };

    const viewOrder = (orderId) => {
        console.log('View order:', orderId);
    };

    return (
        <>
            <Header />
            <DashboardContainer>
                <Row>
                    <Col span={24}>
                        <div>
                            {/* Keep only the title in the first row */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <BaseTypography
                                    style={{
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        textAlign: 'left',
                                    }}
                                >
                                    Dashboard
                                </BaseTypography>
                            </div>

                            {/* Move tags to a new row below the title */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                                {unfulfilledOrders.length > 0 && (
                                    <Tag
                                        color="red"
                                        style={{ cursor: 'pointer', marginRight: '0', padding: '3px' }}
                                        onClick={() => {
                                            ordersSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        {unfulfilledOrders.length} Unfulfilled
                                    </Tag>
                                )}
                                {upcomingOrConfirmed.length > 0 && (
                                    <Tag
                                        color="green"
                                        style={{ cursor: 'pointer', marginRight: '0', padding: '3px' }}
                                        onClick={() => {
                                            bookingsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        {upcomingOrConfirmed.length} Bookings
                                    </Tag>
                                )}
                            </div>

                            {/* Keep filters and date picker as is */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '17px', marginBottom: '17px' }}>
                                <DashboardFilters
                                    onFilterChange={handleFilterChange}
                                    defaultFilter={timeframe}
                                />
                                <RangePicker
                                    style={{ height: '48px' }}
                                    onChange={handleDateRangeChange}
                                    value={selectedDateRange}
                                    allowClear
                                />
                            </div>
                        </div>
                    </Col>

                    {loading ? (
                        <Col span={24} style={{ textAlign: 'center', padding: '48px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                        </Col>
                    ) : (
                        <>
                            {/* Metric Cards */}
                            <Row>
                                <Col span={24} style={{ marginBottom: '24px' }}>
                                    <Row gutter={[8, 8]}> 
                                        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                                            <MetricCardComponent
                                                metrics={totalMetrics}
                                                filteredData={filteredData}
                                                chartDataKey={chartDataKey}
                                                setChartDataKey={setChartDataKey}
                                                type="revenue"
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                                            <MetricCardComponent
                                                metrics={totalMetrics}
                                                filteredData={filteredData}
                                                chartDataKey={chartDataKey}
                                                setChartDataKey={setChartDataKey}
                                                type="visits"
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>

                            {/* Chart */}
                            <Col xs={24}>
                                <ChartContainer>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <AreaChart
                                            data={filteredData}
                                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                        >
                                            {/* 1) Dynamically pick colors for the gradient based on chartDataKey */}
                                            <defs>
                                                <linearGradient id="colorLineFade" x1="0" y1="0" x2="0" y2="1">
                                                    <stop
                                                        offset="5%"
                                                        stopColor={chartDataKey === 'visits' ? '#1890ff' : '#28a745'}
                                                        stopOpacity={0.8}
                                                    />
                                                    <stop
                                                        offset="95%"
                                                        stopColor={chartDataKey === 'visits' ? '#1890ff' : '#28a745'}
                                                        stopOpacity={0}
                                                    />
                                                </linearGradient>
                                            </defs>

                                            <CartesianGrid stroke="#ccc" strokeWidth={0.5} vertical={false} />
                                            <XAxis dataKey="date" />
                                            <YAxis
                                                width={30}
                                                axisLine={false}
                                                tickLine={false}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => {
                                                    // Keep your existing logic:
                                                    if (name === "netRevenue") {
                                                        return [`$${Number(value).toFixed(2)}`, "Revenue"];
                                                    }
                                                    return [value, name];
                                                }}
                                                labelFormatter={(label) => `Date: ${label}`}
                                                contentStyle={{
                                                    backgroundColor: '#fff',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    padding: '8px'
                                                }}
                                            />

                                            {/* 2) Also dynamically set stroke color based on chartDataKey */}
                                            <Area
                                                type="monotone"
                                                dataKey={chartDataKey}
                                                stroke={chartDataKey === 'visits' ? '#1890ff' : '#28a745'}
                                                fill="url(#colorLineFade)"
                                                strokeWidth={3}
                                                isAnimationActive={true}
                                                animationDuration={1500}
                                                animationEasing="ease-in"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </ChartContainer>
                            </Col>

                            {/* Orders Section with Tabs */}
                            <Col xs={24}>
                                <OrdersOverview
                                    unfulfilledOrders={unfulfilledOrders}
                                    fulfilledOrders={fulfilledOrders}
                                    upcomingBookings={upcomingOrConfirmed}
                                    completedBookings={completed}
                                    onFulfill={handleFulfillOrder}
                                    onView={viewOrder}
                                    onViewFulfillment={handleViewFulfillment}
                                    refreshBookings={refreshBookings}
                                />
                            </Col>
                        </>
                    )}
                </Row>
            </DashboardContainer>

            <BottomNavBar />

            {showFulfillmentModal && (
                <OrderFulfillmentModal
                    visible={showFulfillmentModal}
                    order={selectedOrder}
                    onCancel={() => {
                        setShowFulfillmentModal(false);
                        setSelectedOrder(null);
                    }}
                    onUpdate={() => {
                        refreshOrders();
                        setShowFulfillmentModal(false);
                        setSelectedOrder(null);
                    }}
                    updateOrderStatus={updateOrderStatus}
                />
            )}

            {showFulfillmentDetailsModal && (
                <FulfillmentDetailsModal
                    visible={showFulfillmentDetailsModal}
                    order={selectedFulfillmentOrder}
                    onCancel={() => {
                        setShowFulfillmentDetailsModal(false);
                        setSelectedFulfillmentOrder(null);
                    }}
                />
            )}
        </>
    );
}

export default Dashboard;
